import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {useHistory} from "react-router-dom"
import {Container} from "../../../components/Container"
import {useRPC} from "../../../hooks/rpc"
import {EnergyModel} from "../../../store/control/state"
import {backButtonToggle} from "../../../store/ui/actions"
import {onClickHandler} from "../../../utils/onclick"
import {PhaseField} from "../components/PhaseField"
import {PhaseModel} from "../types/types"
import {useLocalization} from "../../../hooks/localization"
import "./index.scss"
import {SelectItem} from "../../../components/Select"
import {DateFilterPopup, getDaysInMonth, initialYear} from "../../StatisticScreens/TemperatureStatistic"
import useStaticData from "../data/static"
import {useOutsideDetector} from "../../../hooks/outside-detector"
import {SettingSelectComponent} from "../../../components/Settings/SettingSelectComponent"
import fullscreen from "../../../assets/images/icons/fullscreen.png"

const BACK_URL = "/energy"
const date = new Date()
export const EnergyAmount = memo(function EnergyAmount() {
    const [rpc] = useRPC()
    const [barChartLabel, setBarChartLabel] = useState("hour")
    const [energyHourlyStatistic, setEnergyHourlyStatistic] = useState<EnergyModel[]>([])
    const [energyDailyStatistic, setEnergyDailyStatistic] = useState<EnergyModel[]>([])
    const [energyMonthlyStatistic, setEnergyMonthlyStatistic] = useState<EnergyModel[]>([])
    const controls = useSelector(state => state.control.controlList)
    const controlList = controls.filter(control => control.type_id === "200")
    const Phases: PhaseModel[] = useMemo(() => controlList, [controlList])
    const [selectedPhase, setSelectedPhase] = useState({
        id: Phases[0]?.id || undefined,
        selected: true
    })
    const energyControlList = useSelector(state => state.control.energyState)
    const history = useHistory()
    const {t} = useLocalization()
    const dispatch = useDispatch()
    dispatch(backButtonToggle({is_open: true, to: "/energy"}))
    const [openPopup, setOpenPopup] = useState(false)

    const {months} = useStaticData()

    const [selectedFullDate, setSelectedFullDate] = useState<string>(
        `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`
      );

    const [selectedDay, setSelectedDay] = useState<SelectItem<string>>(null)
    const [selectedMonth, setSelectedMonth] = useState<SelectItem<string>>(null)
    const [selectedYear, setSelectedYear] = useState<SelectItem<number>>(null)

    const filterDates = useCallback(() => {
        if (selectedDay == null || selectedMonth == null || selectedYear == null) {
            return
        }
        const newDate = `${selectedDay?.value}.${selectedMonth?.value}.${selectedYear.value}`
        setSelectedFullDate(newDate)
        setOpenPopup(false)
    }, [selectedDay, selectedMonth, selectedYear])

    const calculateYear: SelectItem<number>[] = useMemo(
        () =>
            Array.from({length: date.getFullYear() - initialYear + 1}, (_, index) => {
                return {label: (initialYear + index).toString(), value: initialYear + index}
            }) as [],
        []
    )

    const calculateMonth: SelectItem<string>[] = useMemo(
        () =>
            months.map((month, index) => {
                if (index >= 0 && index < 9) {
                    return {label: month, value: `0${(index + 1).toString()}`}
                }
                return {value: (index + 1).toString(), label: month}
            }),
        [months]
    )

    const renderDays: SelectItem<string>[] = useMemo(() => {
        if (selectedMonth?.value && selectedYear?.value) {
            const daysInSelectedMonth = getDaysInMonth(Number(selectedMonth?.value), selectedYear?.value)
            return Array.from({length: daysInSelectedMonth}, (_, index) => {
                if (index >= 0 && index < 9) {
                    return {label: (index + 1).toString(), value: `0${(index + 1).toString()}`}
                }
                return {label: (index + 1).toString(), value: (index + 1).toString()}
            })
        }
        return []
    }, [selectedMonth, selectedYear])

    const clickOutside = useOutsideDetector(openPopup, () => setOpenPopup(false))

    const queryStatisticType = useMemo(() => {
        switch (barChartLabel) {
            case "hour":
                return energyHourlyStatistic
            case "day":
                return energyDailyStatistic
            case "month":
                return energyMonthlyStatistic
        }
    }, [barChartLabel, energyHourlyStatistic, energyDailyStatistic, energyMonthlyStatistic])

    const selectedAmount = useCallback((list: EnergyModel[], id: string) => {
        return list.find(r => (r?.control_id === id ? r?.price : 0))?.price
    }, [])

    const onClickRequestEnergyStatistic = useCallback(() => {
        rpc.EnergyHourlyStatistics(selectedFullDate).then(data => setEnergyHourlyStatistic(data || []))
        rpc.EnergyDailyStatistics(selectedFullDate).then(data => setEnergyDailyStatistic(data || []))
        rpc.EnergyMonthlyStatistics(selectedFullDate).then(data => setEnergyMonthlyStatistic(data || []))
    }, [rpc, selectedFullDate])

    const [amountValue, setAmountValue] = useState(selectedAmount(energyHourlyStatistic, selectedPhase?.id))
    const onClickAmountStatus = useCallback(
        (action: number) => {
            let newChartLabel = barChartLabel
            let newChartAmount = amountValue
            if (action === 1) {
                newChartLabel = "hour"
            } else if (action === 2) {
                newChartLabel = "day"
            } else if (action === 3) {
                newChartLabel = "month"
            }
            setBarChartLabel(newChartLabel)
            if (newChartLabel === "hour") {
                newChartAmount = selectedAmount(energyHourlyStatistic, selectedPhase?.id)
            } else if (newChartLabel === "day") {
                newChartAmount = selectedAmount(energyDailyStatistic, selectedPhase?.id)
            } else if (newChartLabel === "month") {
                newChartAmount = selectedAmount(energyMonthlyStatistic, selectedPhase?.id)
            }
            setAmountValue(newChartAmount)
            onClickRequestEnergyStatistic()
        },
        [
            onClickRequestEnergyStatistic,
            selectedAmount,
            barChartLabel,
            amountValue,
            energyDailyStatistic,
            energyHourlyStatistic,
            energyMonthlyStatistic,
            selectedPhase?.id
        ]
    )
    useEffect(
        () => setAmountValue(selectedAmount(queryStatisticType, selectedPhase?.id)),
        [selectedAmount, queryStatisticType, selectedPhase?.id]
    )

    const onClickPhaseChanging = useCallback(
        (id: string) => {
            Phases.find(
                phase =>
                    phase.id === id &&
                    setSelectedPhase({
                        id: id,
                        selected: true
                    })
            )
            onClickRequestEnergyStatistic()
        },
        [onClickRequestEnergyStatistic, Phases]
    )

    const amountType = useMemo(() => {
        switch (energyControlList.currency_unit) {
            case "try":
                return (
                    <>
                        <span>{amountValue?.toFixed(2)}</span>
                        <span>₺</span>
                    </>
                )
            case "usd":
                return (
                    <>
                        <span>$</span>
                        <span>{amountValue?.toFixed(2)}</span>
                    </>
                )
            case "eur":
                return (
                    <>
                        <span>€</span>
                        <span>{amountValue?.toFixed(2)}</span>
                    </>
                )
            case "gbp":
                return (
                    <>
                        <span>£</span>
                        <span>{amountValue?.toFixed(2)}</span>
                    </>
                )
        }
    }, [energyControlList, amountValue])

    useEffect(() => {
        onClickRequestEnergyStatistic()
    }, [onClickRequestEnergyStatistic])

    return (
        <>
            {openPopup && (
                <DateFilterPopup outsideClick={clickOutside}>
                    <div className="picker-field">
                        <span>{t("year")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={calculateYear}
                            onChange={setSelectedYear}
                            value={selectedYear}
                        />
                    </div>
                    <div className="picker-field">
                        <span>{t("month")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={calculateMonth}
                            onChange={setSelectedMonth}
                            value={selectedMonth}
                        />
                    </div>
                    <div className="picker-field">
                        <span>{t("day")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={renderDays}
                            onChange={setSelectedDay}
                            value={selectedDay}
                        />
                    </div>
                    <div className="picker-field" style={{height: "100px"}}>
                        <button onClick={filterDates}>{t("select")}</button>
                    </div>
                </DateFilterPopup>
            )}
            <Container>
                <div className="page-wrapper">
                    <div className="energy-wrapper">
                        <div className="energy-page-container">
                            <PhaseField
                                selectedPhase={selectedPhase}
                                //onClickAllPhase={onClickAllPhase}
                                onClickPhaseChanging={onClickPhaseChanging}
                            />
                        </div>
                        <div className="energy-page-full-screen-container">
                            <div className="energy-page-full-screen">
                                <span className="energy-top-action">
                                    <span className="energy-top-action-text">
                                        <div className="date-filter-button" onClick={() => setOpenPopup(true)}>
                                            {t("pick_date")}
                                        </div>
                                    </span>
                                    <span className="energy-top-action-button">
                                        <button
                                            {...onClickHandler(() => onClickAmountStatus(1))}
                                            className={`${barChartLabel === "hour" ? "energy-active-button" : ""}`}
                                        >
                                            {t("energy_page.hour")}
                                        </button>
                                        <button
                                            {...onClickHandler(() => onClickAmountStatus(2))}
                                            className={`${barChartLabel === "day" ? "energy-active-button" : ""}`}
                                        >
                                            {t("energy_page.day")}
                                        </button>
                                        <button
                                            {...onClickHandler(() => onClickAmountStatus(3))}
                                            className={`${barChartLabel === "month" ? "energy-active-button" : ""}`}
                                        >
                                            {t("energy_page.month")}
                                        </button>
                                        <img className="energy-fullscreen" src={fullscreen} alt="" onClick={() => history.push(BACK_URL)}/>
                                    </span>
                                </span>
                                <div className="fullscreen-amount-screen" onDoubleClick={() => history.push(BACK_URL)}>
                                    <div>{amountType}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </>
    )
})
